import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AccountDetail, AIReplySettings } from '../types';
import {
  getAccountDetails,
  updateAccountStatus,
  deleteAccount,
  generateQRLogin,
  checkQRLoginStatus,
  generateBrowserQRLogin,
  checkBrowserQRLoginStatus,
  closeBrowserQRLogin,
  updateAccountRemark,
  updateAccountAutoConfirm,
  updateAccountPauseDuration,
  updateAccountCookie,
  updateAccountLoginInfo,
  updateAccountAISettings,
  getAllAISettings,
  getAccountAISettings,
  addAccountByCookie,
  ManualCaptchaSession,
  startManualCaptcha,
  refreshManualCaptcha,
  closeManualCaptcha,
  sendManualCaptchaDrag
} from '../services/api';
import { LocalSlider, SliderPoint } from './LocalSlider';
import {
  Plus, Power, Edit2, Trash2, QrCode, X, Check, Loader2,
  MessageSquare, RefreshCw, Save, User, Clock, MessageCircle,
  Upload, Key, Eye, EyeOff, Bot, Settings, Cookie, MousePointer2
} from 'lucide-react';

type ModalType = 'edit' | 'ai-settings' | 'cookie-login' | null;

const AccountList: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [loading, setLoading] = useState(true);
  /** 首次账号列表是否已拉取过；避免后续 loadAccounts 再全屏 loading 把扫码弹窗整棵卸掉，看起来像二维码秒换 */
  const [listReady, setListReady] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  /** 当前展示的这一张二维码对应的 session，用于 img key，避免父级重绘时不必要地重解码 */
  const [qrImageSessionId, setQrImageSessionId] = useState<string>('');
  const [qrStatus, setQrStatus] = useState<string>('pending');
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [editingAccount, setEditingAccount] = useState<AccountDetail | null>(null);

  // 编辑表单状态
  const [editForm, setEditForm] = useState({
    remark: '',
    cookie: '',
    auto_confirm: false,
    pause_duration: 0,
    username: '',
    login_password: '',
    show_browser: false,
    showLoginPassword: false,
  });

  // AI设置表单状态
  const [aiSettings, setAiSettings] = useState<AIReplySettings>({
    ai_enabled: false,
    max_discount_percent: 10,
    max_discount_amount: 100,
    max_bargain_rounds: 3,
    custom_prompts: '',
  });
  const [saving, setSaving] = useState(false);
  // Cookie 登录表单状态
  const [cookieLoginValue, setCookieLoginValue] = useState('');
  const [cookieLoginError, setCookieLoginError] = useState('');
  const [qrVerificationUrl, setQrVerificationUrl] = useState<string | null>(null);
  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollSessionIdRef = useRef<string | null>(null);
  const qrBusyRef = useRef(false);
  const lastQrGenerateAtRef = useRef(0);
  const qrLoginKindRef = useRef<'http' | 'browser'>('http');
  const [qrActiveKind, setQrActiveKind] = useState<'http' | 'browser'>('http');
  const [qrBrowserHint, setQrBrowserHint] = useState('');
  const [qrBrowserLoginUrl, setQrBrowserLoginUrl] = useState('');
  const [qrErrorDetail, setQrErrorDetail] = useState('');
  /** 扫码成功后后台异步入库，单次 loadAccounts 可能仍读到旧数据；延迟多轮刷新并通知仪表盘 */
  const qrRefreshTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [manualCaptcha, setManualCaptcha] = useState<ManualCaptchaSession | null>(null);
  const [manualCaptchaAccountId, setManualCaptchaAccountId] = useState('');
  const [manualCaptchaError, setManualCaptchaError] = useState('');
  const [manualCaptchaRefreshing, setManualCaptchaRefreshing] = useState(false);
  const [manualCaptchaSubmitting, setManualCaptchaSubmitting] = useState(false);
  const manualCaptchaSessionRef = useRef<string | null>(null);

  const clearQrRefreshTimeouts = () => {
    qrRefreshTimeoutsRef.current.forEach(clearTimeout);
    qrRefreshTimeoutsRef.current = [];
  };

  const notifyAccountsChanged = () => {
    window.dispatchEvent(new CustomEvent('goofish:accounts-changed'));
  };

  const schedulePostQrAccountRefresh = () => {
    clearQrRefreshTimeouts();
    const run = () => {
      loadAccounts();
      notifyAccountsChanged();
    };
    const delays = [0, 2000, 5000, 10000, 18000, 28000];
    delays.forEach((ms) => {
      const t = setTimeout(run, ms);
      qrRefreshTimeoutsRef.current.push(t);
    });
  };

  const loadAccounts = async () => {
    if (!listReady) setLoading(true);
    try {
      const data = await getAccountDetails();

      // 获取所有账号的AI设置
      let allAISettings: Record<string, AIReplySettings> = {};
      try {
        allAISettings = await getAllAISettings();
      } catch (e) {
        console.error('Failed to load AI settings:', e);
      }

      // 获取 WebSocket 连接状态
      let wsStatusMap: Record<string, { connection_state: string; ws_active: boolean }> = {};
      try {
        const statusRes = await fetch('/api/system-status');
        const statusData = await statusRes.json();
        if (statusData.instances) {
          for (const inst of statusData.instances) {
            wsStatusMap[inst.cookie_id] = { connection_state: inst.connection_state, ws_active: inst.ws_active };
          }
        }
      } catch (e) {
        console.error('Failed to load system status:', e);
      }

      // 合并AI设置和WS状态到账号数据
      const accountsWithAI = data.map(account => ({
        ...account,
        ws_connection_state: wsStatusMap[account.id]?.connection_state || 'unknown',
        ws_active: wsStatusMap[account.id]?.ws_active ?? false,
        ai_enabled: allAISettings[account.id]?.ai_enabled ?? false,
        max_discount_percent: allAISettings[account.id]?.max_discount_percent ?? 10,
        max_discount_amount: allAISettings[account.id]?.max_discount_amount ?? 100,
        max_bargain_rounds: allAISettings[account.id]?.max_bargain_rounds ?? 3,
        custom_prompts: allAISettings[account.id]?.custom_prompts ?? '',
      }));

      setAccounts(accountsWithAI);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
      setListReady(true);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    return () => {
      const sessionId = pollSessionIdRef.current;
      const manualSessionId = manualCaptchaSessionRef.current;
      if (qrLoginKindRef.current === 'browser' && sessionId) {
        void closeBrowserQRLogin(sessionId).catch(() => undefined);
      }
      if (manualSessionId) {
        void closeManualCaptcha(manualSessionId).catch(() => undefined);
      }
      if (qrPollRef.current) {
        clearInterval(qrPollRef.current);
        qrPollRef.current = null;
      }
      pollSessionIdRef.current = null;
      clearQrRefreshTimeouts();
    };
  }, []);

  const stopQRPolling = () => {
    if (qrPollRef.current) {
      clearInterval(qrPollRef.current);
      qrPollRef.current = null;
    }
    pollSessionIdRef.current = null;
  };

  const closeQRModal = () => {
    const sessionId = pollSessionIdRef.current;
    const isBrowserSession = qrLoginKindRef.current === 'browser';
    clearQrRefreshTimeouts();
    stopQRPolling();
    if (isBrowserSession && sessionId) {
      void closeBrowserQRLogin(sessionId).catch(() => undefined);
    }
    setShowQRModal(false);
    setQrCodeUrl('');
    setQrImageSessionId('');
    setQrVerificationUrl(null);
    setQrStatus('pending');
    setQrBrowserHint('');
    setQrBrowserLoginUrl('');
    setQrErrorDetail('');
    qrLoginKindRef.current = 'http';
    setQrActiveKind('http');
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    await updateAccountStatus(id, !currentStatus);
    loadAccounts();
  };

  const handleDelete = async (id: string) => {
    if (confirm('确认删除该账号吗？')) {
      await deleteAccount(id);
      loadAccounts();
    }
  };

  const openEditModal = (account: AccountDetail) => {
    setEditingAccount(account);
    setEditForm({
      remark: account.remark || account.note || '',
      cookie: account.cookie || account.value || '',
      auto_confirm: account.auto_confirm || false,
      pause_duration: account.pause_duration || 0,
      username: account.username || '',
      login_password: account.login_password || '',
      show_browser: account.show_browser || false,
      showLoginPassword: false,
    });
    setActiveModal('edit');
  };

  const openAIModal = async (account: AccountDetail) => {
    setEditingAccount(account);
    setSaving(true);
    try {
      const settings = await getAccountAISettings(account.id);
      setAiSettings({
        ai_enabled: settings.ai_enabled ?? false,
        max_discount_percent: settings.max_discount_percent ?? 10,
        max_discount_amount: settings.max_discount_amount ?? 100,
        max_bargain_rounds: settings.max_bargain_rounds ?? 3,
        custom_prompts: settings.custom_prompts ?? '',
      });
    } catch (e) {
      console.error('Failed to load AI settings:', e);
    } finally {
      setSaving(false);
    }
    setActiveModal('ai-settings');
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;
    setSaving(true);

    try {
      const promises: Promise<any>[] = [];

      // 更新备注
      if (editForm.remark !== (editingAccount.remark || editingAccount.note || '')) {
        promises.push(updateAccountRemark(editingAccount.id, editForm.remark));
      }

      // 更新Cookie
      if (editForm.cookie && editForm.cookie !== (editingAccount.cookie || editingAccount.value || '')) {
        promises.push(updateAccountCookie(editingAccount.id, editForm.cookie));
      }

      // 更新自动确认
      if (editForm.auto_confirm !== editingAccount.auto_confirm) {
        promises.push(updateAccountAutoConfirm(editingAccount.id, editForm.auto_confirm));
      }

      // 更新暂停时长
      if (editForm.pause_duration !== (editingAccount.pause_duration || 0)) {
        promises.push(updateAccountPauseDuration(editingAccount.id, editForm.pause_duration));
      }

      // 更新登录信息
      if (
        editForm.username !== (editingAccount.username || '') ||
        editForm.login_password !== (editingAccount.login_password || '') ||
        editForm.show_browser !== (editingAccount.show_browser || false)
      ) {
        promises.push(updateAccountLoginInfo(editingAccount.id, {
          username: editForm.username,
          login_password: editForm.login_password,
          show_browser: editForm.show_browser,
        }));
      }

      await Promise.all(promises);
      setActiveModal(null);

      const cookieChanged = editForm.cookie && editForm.cookie !== (editingAccount.cookie || editingAccount.value || '');
      if (cookieChanged) {
        // Cookie 已更新，后端在独立线程重启任务（需要几秒）
        // 立即在前端把该账号状态设为"连接中"，避免显示"未连接"
        setAccounts((prev: AccountDetail[]) => prev.map((a: AccountDetail) =>
          a.id === editingAccount.id
            ? { ...a, ws_connection_state: 'connecting', need_qr_relogin: false }
            : a
        ));
        // 3秒后从后端刷新真实状态
        setTimeout(() => loadAccounts(), 3000);
      } else {
        loadAccounts();
      }
    } catch (error) {
      console.error('更新账号失败:', error);
      alert('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAISettings = async () => {
    if (!editingAccount) return;
    setSaving(true);

    try {
      await updateAccountAISettings(editingAccount.id, aiSettings);
      setActiveModal(null);
      loadAccounts();
    } catch (error) {
      console.error('更新AI设置失败:', error);
      alert('更新失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const startQRLogin = async (force = false, kind: 'http' | 'browser' = 'http') => {
    const now = Date.now();
    if (!force && lastQrGenerateAtRef.current > 0 && now - lastQrGenerateAtRef.current < 5000) {
      return;
    }
    if (qrBusyRef.current) return;
    qrBusyRef.current = true;
    qrLoginKindRef.current = kind;
    setQrActiveKind(kind);
    clearQrRefreshTimeouts();
    stopQRPolling();
    setQrVerificationUrl(null);
    setQrBrowserHint('');
    setQrBrowserLoginUrl('');
    setQrErrorDetail('');
    setShowQRModal(true);
    setQrStatus('loading');
    try {
      const res =
        kind === 'browser'
          ? await generateBrowserQRLogin(true)
          : await generateQRLogin();

      const okHttp = kind === 'http' && res.success && 'qr_code_url' in res && res.qr_code_url && res.session_id;
      const okBrowser = kind === 'browser' && res.success && res.session_id;

      if (okHttp || okBrowser) {
        const sessionId = res.session_id!;
        lastQrGenerateAtRef.current = Date.now();
        pollSessionIdRef.current = sessionId;
        if (kind === 'http') {
          setQrCodeUrl((res as { qr_code_url: string }).qr_code_url);
        } else {
          setQrCodeUrl(res.qr_code_url || '');
          setQrBrowserHint((res as { message?: string }).message || '');
          setQrBrowserLoginUrl((res as { login_url?: string }).login_url || '');
        }
        setQrImageSessionId(sessionId);
        setQrStatus('waiting');

        let browserNotFoundStreak = 0;
        const interval = setInterval(async () => {
          if (pollSessionIdRef.current !== sessionId) return;
          try {
            const checkFn =
              qrLoginKindRef.current === 'browser'
                ? checkBrowserQRLoginStatus
                : checkQRLoginStatus;
            const statusRes = await checkFn(sessionId);
            if (pollSessionIdRef.current !== sessionId) return;
            if (
              qrLoginKindRef.current === 'browser' &&
              typeof statusRes.qr_code_url === 'string' &&
              statusRes.qr_code_url.length > 50
            ) {
              setQrCodeUrl(statusRes.qr_code_url);
            }
            if (statusRes.status === 'processing') {
              return;
            }
            if (statusRes.status === 'success' || statusRes.status === 'already_processed') {
              stopQRPolling();
              setQrStatus('success');
              setTimeout(() => {
                closeQRModal();
                schedulePostQrAccountRefresh();
              }, 600);
            } else if (statusRes.status === 'scanned') {
              setQrStatus('scanned');
            } else if (statusRes.status === 'verification_required') {
              // 勿停止轮询：后端在用户完成人脸/验证后会继续 query.do，前端需持续 check 才能收到 success
              const url = statusRes.verification_url as string | undefined;
              if (url) setQrVerificationUrl(url);
              setQrStatus('verification_required');
            } else if (statusRes.status === 'not_found' && qrLoginKindRef.current === 'browser') {
              browserNotFoundStreak += 1;
              // 多 worker / 偶发竞态时前几轮可能 not_found，稍后再试
              if (browserNotFoundStreak >= 8) {
                stopQRPolling();
                setQrStatus('error');
                setQrErrorDetail(
                  '找不到扫码会话。若使用多进程/多副本部署，浏览器扫码会话仅在创建它的那一个进程内有效，请改为单进程或固定路由到同一实例。'
                );
              }
            } else if (
              statusRes.status === 'expired' ||
              statusRes.status === 'error' ||
              statusRes.status === 'cancelled' ||
              statusRes.status === 'not_found'
            ) {
              stopQRPolling();
              setQrStatus('error');
              const msg = (statusRes as { message?: string }).message;
              if (msg) setQrErrorDetail(msg);
            }
          } catch (err) {
            if (pollSessionIdRef.current !== sessionId) return;
            stopQRPolling();
            setQrStatus('error');
            setQrErrorDetail(err instanceof Error ? err.message : String(err));
          }
        }, 2000);
        qrPollRef.current = interval;
      } else {
        setQrStatus('error');
        if (kind === 'browser' && res && typeof res === 'object') {
          const m = (res as { message?: string }).message;
          if (m) setQrErrorDetail(m);
          else if (!res.success) setQrErrorDetail('创建浏览器扫码失败，请查看后端日志（常见原因：未安装 Chromium，执行 playwright install chromium）');
        }
      }
    } catch (e) {
      setQrStatus('error');
      if (qrLoginKindRef.current === 'browser') {
        setQrErrorDetail(e instanceof Error ? e.message : String(e));
      }
    } finally {
      qrBusyRef.current = false;
    }
  };

  const applyManualCaptchaResult = (result: ManualCaptchaSession) => {
    // Real-time move responses only include a frame when a new remote preview
    // is ready. Preserve the preceding frame between previews.
    setManualCaptcha((current) => ({
      ...(current ?? {}),
      ...result,
      screenshot: result.screenshot ?? (
        result.status === 'completed' ? undefined : current?.screenshot
      ),
    }));
    if (result.status === 'completed') {
      manualCaptchaSessionRef.current = null;
      setManualCaptchaSubmitting(false);
      setTimeout(() => {
        loadAccounts();
        notifyAccountsChanged();
      }, 1200);
    }
  };

  const closeManualCaptchaModal = async () => {
    const sessionId = manualCaptchaSessionRef.current;
    manualCaptchaSessionRef.current = null;
    setManualCaptchaSubmitting(false);
    setManualCaptcha(null);
    setManualCaptchaError('');
    setManualCaptchaAccountId('');
    if (sessionId) {
      await closeManualCaptcha(sessionId).catch(() => undefined);
    }
  };

  const startAccountManualCaptcha = async (accountId: string) => {
    await closeManualCaptchaModal();
    setManualCaptchaAccountId(accountId);
    setManualCaptcha({
      session_id: '',
      status: 'starting',
      message: '正在获取新的滑块挑战',
    });
    try {
      const result = await startManualCaptcha(accountId);
      manualCaptchaSessionRef.current = result.session_id;
      applyManualCaptchaResult(result);
      // 不再需要 WebSocket 连接
    } catch (error) {
      const message = error instanceof Error ? error.message : '无法开启人工验证';
      setManualCaptchaError(message);
      setManualCaptcha({ session_id: '', status: 'error', message });
    }
  };

  const refreshCurrentManualCaptcha = async () => {
    const sessionId = manualCaptchaSessionRef.current;
    if (!sessionId) {
      if (manualCaptchaAccountId) await startAccountManualCaptcha(manualCaptchaAccountId);
      return;
    }
    setManualCaptchaRefreshing(true);
    setManualCaptchaError('');
    try {
      if (manualCaptchaSessionRef.current !== sessionId) return;
      const result = await refreshManualCaptcha(sessionId);
      applyManualCaptchaResult(result);
      // 不再需要 WebSocket 连接
    } catch (error) {
      setManualCaptchaError(error instanceof Error ? error.message : '刷新滑块失败');
    } finally {
      setManualCaptchaRefreshing(false);
    }
  };

  const handleLocalSliderComplete = async (points: SliderPoint[]) => {
    const sessionId = manualCaptchaSessionRef.current;
    if (!sessionId) {
      setManualCaptchaError('会话已失效，请重新获取挑战');
      return;
    }

    if (points.length < 2) {
      setManualCaptchaError('请拖动滑块到最右边');
      return;
    }

    setManualCaptchaSubmitting(true);
    setManualCaptchaError('');

    try {
      const result = await sendManualCaptchaDrag(sessionId, points);
      applyManualCaptchaResult(result);

      if (result.status === 'completed') {
        // 验证成功，1.2秒后关闭弹窗并刷新账号列表
        setTimeout(() => {
          void closeManualCaptchaModal();
          void loadAccounts();
          notifyAccountsChanged();
        }, 1200);
      } else if (result.status === 'waiting') {
        // 验证失败但会话仍可用，显示失败消息
        setManualCaptchaError(result.message || '验证失败，请重试');
      }
    } catch (error) {
      setManualCaptchaError(error instanceof Error ? error.message : '验证失败');
    } finally {
      setManualCaptchaSubmitting(false);
    }
  };

  if (!listReady && loading) {
    return <div className="p-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#FFE815] animate-spin"/></div>;
  }

  return (
    <div className="space-y-8 animate-fade-in relative">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">账号管理</h2>
          <p className="text-gray-500 mt-2 font-medium">管理您的闲鱼授权账号及设置。</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-end">
          <button
            type="button"
            onClick={() => startQRLogin(false, 'http')}
            className="ios-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg shadow-yellow-200 transition-transform hover:scale-105 active:scale-95"
          >
            <QrCode className="w-5 h-5" />
            App 扫码
          </button>
          <button
            type="button"
            onClick={() => startQRLogin(false, 'browser')}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold border-2 border-gray-900 bg-white text-gray-900 shadow-md transition-transform hover:scale-105 active:scale-95"
            title="服务端用 Playwright 打开 m.goofish.com，与 xianyu-auto-bot 同类方案，适合纯 HTTP 扫码拿不到 Cookie 时"
          >
            <QrCode className="w-5 h-5" />
            浏览器扫码
          </button>
          <button
            type="button"
            onClick={() => {
              setCookieLoginValue('');
              setCookieLoginError('');
              setActiveModal('cookie-login');
            }}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold border-2 border-gray-300 bg-white text-gray-700 shadow-sm transition-transform hover:scale-105 active:scale-95"
            title="直接粘贴 Cookie 添加账号"
          >
            <Cookie className="w-5 h-5" />
            Cookie 登录
          </button>
        </div>
      </div>

      {/* Account Grid */}
      <div className="grid grid-cols-1 gap-6">
        {accounts.map((account) => (
          <div key={account.id} className="ios-card p-6 rounded-[2rem] flex items-center justify-between group hover:border-[#FFE815] transition-all duration-300">
            <div className="flex items-center gap-8">
              <div className="relative">
                <img
                  src={account.avatar_url}
                  alt="avatar"
                  className="w-20 h-20 rounded-3xl object-cover shadow-md ring-4 ring-white"
                />
                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${
                    account.need_qr_relogin ? 'bg-amber-500' :
                    !account.enabled ? 'bg-gray-300' :
                    account.ws_connection_state === 'connected' ? 'bg-green-500' :
                    account.ws_connection_state === 'failed' || account.ws_connection_state === 'closed' ? 'bg-red-500' :
                    'bg-blue-400'
                }`}>
                    {account.enabled && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-extrabold text-gray-900">{account.nickname || account.remark || `账号 ${account.id}`}</h3>
                    {account.need_qr_relogin ? (
                        <span className="px-2.5 py-0.5 rounded-lg bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300"
                          title="该账号 Token/滑块多次失败，请重新扫码登录">
                          需重新登录
                        </span>
                    ) : !account.enabled ? (
                        <span className="px-2.5 py-0.5 rounded-lg bg-gray-100 text-gray-500 text-xs font-bold">暂停</span>
                    ) : account.ws_connection_state === 'connected' ? (
                        <span className="px-2.5 py-0.5 rounded-lg bg-green-100 text-green-700 text-xs font-bold">在线</span>
                    ) : account.ws_connection_state === 'connecting' || account.ws_connection_state === 'reconnecting' ? (
                        <span className="px-2.5 py-0.5 rounded-lg bg-blue-100 text-blue-700 text-xs font-bold">连接中</span>
                    ) : account.ws_connection_state === 'failed' || account.ws_connection_state === 'closed' ? (
                        <span className="px-2.5 py-0.5 rounded-lg bg-red-100 text-red-700 text-xs font-bold">连接失败</span>
                    ) : (
                        <span className="px-2.5 py-0.5 rounded-lg bg-yellow-100 text-yellow-700 text-xs font-bold">未连接</span>
                    )}
                    {account.ai_enabled && (
                        <span className="px-2.5 py-0.5 rounded-lg bg-purple-100 text-purple-700 text-xs font-bold flex items-center gap-1">
                          <Bot className="w-3 h-3" /> AI
                        </span>
                    )}
                    {account.runtime_status === 'slider_verifying' && (
                        <span
                          className="px-2.5 py-0.5 rounded-lg bg-orange-100 text-orange-800 text-xs font-bold border border-orange-300 flex items-center gap-1 animate-pulse"
                          title={account.runtime_status_detail || '正在进行滑块验证'}
                        >
                          <Loader2 className="w-3 h-3 animate-spin" /> 滑块验证中
                        </span>
                    )}
                </div>
                <p className="text-sm text-gray-500 font-medium mb-3">{account.remark || account.note || '暂无备注'}</p>
                <div className="flex gap-2">
                   {account.auto_confirm && <span className="text-xs bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5"><MessageSquare className="w-3 h-3"/> 自动回复</span>}
                   {account.pause_duration > 0 && <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5"><Clock className="w-3 h-3"/> 暂停{account.pause_duration}分钟</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={() => openEditModal(account)}
                    className="p-3 rounded-xl hover:bg-gray-100 transition-colors text-gray-600"
                    title="编辑账号"
                >
                    <Edit2 className="w-5 h-5" />
                </button>
                <button
                    onClick={() => openAIModal(account)}
                    className="p-3 rounded-xl hover:bg-purple-100 transition-colors text-purple-600"
                    title="AI设置"
                >
                    <Bot className="w-5 h-5" />
                </button>
                {account.need_qr_relogin && (
                  <button
                    type="button"
                    onClick={() => void startAccountManualCaptcha(account.id)}
                    className="p-3 rounded-xl hover:bg-amber-100 transition-colors text-amber-700"
                    title="人工完成滑块验证"
                    aria-label="人工完成滑块验证"
                  >
                    <MousePointer2 className="w-5 h-5" />
                  </button>
                )}
                <button
                    onClick={() => handleToggle(account.id, account.enabled)}
                    className={`p-3 rounded-xl transition-colors ${account.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                    <Power className="w-5 h-5" />
                </button>
                <button
                    onClick={() => handleDelete(account.id)}
                    className="p-3 rounded-xl hover:bg-red-100 transition-colors text-red-500"
                >
                    <Trash2 className="w-5 h-5" />
                </button>
            </div>
          </div>
        ))}

        {accounts.length === 0 && (
            <div className="ios-card p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">暂无账号</h3>
                <p className="text-gray-500 mt-1">请点击右上角扫码添加您的闲鱼账号</p>
            </div>
        )}
      </div>

      {/* Manual slider modal */}
      {manualCaptcha && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{ maxWidth: 'min(94vw, 720px)' }}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                  <MousePointer2 className="w-6 h-6 text-amber-600" />
                  人工滑块验证
                </h3>
                <p className="text-sm text-gray-500 mt-1">{manualCaptcha.message}</p>
              </div>
              <button
                type="button"
                onClick={() => void closeManualCaptchaModal()}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
                title="关闭"
                aria-label="关闭"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-4">
              {manualCaptchaError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm font-medium">
                  {manualCaptchaError}
                </div>
              )}

              <div className="relative w-full min-h-[220px] bg-[#F7F8FA] rounded-2xl border-4 border-white shadow-inner overflow-hidden flex items-center justify-center">
                {manualCaptcha.status === 'starting' && (
                  <Loader2 className="w-9 h-9 text-[#FFE815] animate-spin" />
                )}
                {manualCaptcha.status === 'waiting' && (
                  <div className="w-full space-y-4 p-6">
                    {manualCaptcha.screenshot && (
                      <div className="mb-4">
                        <img
                          src={manualCaptcha.screenshot}
                          alt="闲鱼滑块验证背景"
                          draggable={false}
                          className="block w-full h-auto select-none pointer-events-none rounded-lg"
                        />
                      </div>
                    )}
                    <LocalSlider
                      onComplete={handleLocalSliderComplete}
                      disabled={manualCaptchaSubmitting || manualCaptchaRefreshing}
                      buttonSize={manualCaptcha.button_size}
                      trackHeight={manualCaptcha.track_height}
                    />
                    {manualCaptchaSubmitting && (
                      <div className="py-4 flex flex-col items-center text-gray-500">
                        <Loader2 className="w-8 h-8 mb-2 animate-spin" />
                        <span className="text-sm">正在验证...</span>
                      </div>
                    )}
                  </div>
                )}
                {manualCaptcha.status === 'completed' && (
                  <div className="py-12 flex flex-col items-center text-green-600">
                    <Check className="w-10 h-10 mb-3" />
                    <span className="font-bold">验证成功</span>
                  </div>
                )}
                {manualCaptcha.status === 'error' && !manualCaptchaError && (
                  <p className="px-6 text-center text-sm text-red-600">{manualCaptcha.message}</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  type="button"
                  onClick={() => void closeManualCaptchaModal()}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void refreshCurrentManualCaptcha()}
                  disabled={manualCaptchaRefreshing || manualCaptchaSubmitting || manualCaptcha.status === 'completed'}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-amber-100 text-amber-900 hover:bg-amber-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {manualCaptchaRefreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  获取新挑战
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* QR Code Modal */}
      {showQRModal && createPortal(
          <div className="modal-overlay-centered">
              <div
                className="modal-container"
                style={{
                  maxWidth:
                    qrActiveKind === 'browser'
                      ? 'min(96vw, 72rem)'
                      : qrStatus === 'verification_required'
                        ? '32rem'
                        : '24rem',
                  maxHeight: qrActiveKind === 'browser' ? '92vh' : undefined,
                }}
              >
                  <button
                    type="button"
                    onClick={closeQRModal}
                    className="self-end p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors mb-6"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>

                  <div className="modal-body">
                      <div className="text-center">
                          <h3 className="text-2xl font-extrabold text-gray-900 mb-2">
                            {qrActiveKind === 'browser' ? '浏览器扫码登录' : '扫码登录'}
                          </h3>
                          {qrStatus === 'verification_required' ? (
                            <div className="text-left space-y-4 mb-6">
                              <p className="text-gray-600 font-medium text-sm leading-relaxed">
                                下方灰框内是<span className="text-gray-900 font-bold">嵌入的闲鱼官网页面</span>（与浏览器新开标签打开同一链接是同一套内容），因此「身份校验流程已结束」等文案是<span className="text-gray-900 font-bold">闲鱼返回的</span>，并非本系统提示。校验常在手机 App 内已完成，框内会直接进入结束态；请<span className="text-gray-900 font-bold">不要关本弹窗</span>，等待自动登录成功。若框内异常，请用下方按钮在<strong>新窗口</strong>打开同一验证页。
                              </p>
                              {qrVerificationUrl ? (
                                <>
                                  <a
                                    href={qrVerificationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-full ios-btn-primary py-3 rounded-2xl font-bold text-sm"
                                  >
                                    在新窗口打开验证页
                                  </a>
                                  <p className="text-xs text-gray-400 break-all font-mono bg-gray-50 p-2 rounded-lg">{qrVerificationUrl}</p>
                                </>
                              ) : (
                                <p className="text-amber-700 text-sm font-bold">未返回验证链接，请关闭后重新扫码。</p>
                              )}
                              <p className="text-xs text-gray-500">
                                框内页面由闲鱼域名直接提供。若中文显示为乱码，请将浏览器<strong>首选语言</strong>设为「中文」，或使用上方「在新窗口打开验证页」；与服务器地区无关。
                              </p>
                              <div className="w-full h-[min(50vh,22rem)] rounded-2xl border border-gray-200 overflow-hidden bg-gray-100">
                                {qrVerificationUrl ? (
                                  <iframe
                                    title="闲鱼安全验证"
                                    src={qrVerificationUrl}
                                    className="w-full h-full border-0"
                                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                  />
                                ) : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => startQRLogin(true, qrActiveKind)}
                                className="w-full py-3 rounded-2xl font-bold bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                              >
                                长时间无反应时再试：重新生成二维码
                              </button>
                            </div>
                          ) : qrActiveKind === 'browser' ? (
                            <>
                              <div className="text-gray-600 mb-4 text-sm text-left leading-relaxed space-y-2">
                                <p>
                                  <strong className="text-gray-900">登录发生在服务端的无头浏览器里</strong>
                                  ，不是你的浏览器。闲鱼 App 扫到的二维码内容，与当时服务端页面里的一致即可解码；服务端会轮询该浏览器是否已登录（与 xianyu-auto-bot 同类）。
                                </p>
                                <p>
                                  下方图片只是<strong className="text-gray-900">那一刻的页面截图</strong>
                                  ，方便你在本机看到码。扫截图与扫真机屏幕上的码<strong>一样有效</strong>
                                  ，只要码未过期。若闲鱼频繁刷新二维码，请点击「重试」重新生成会话以拿到新截图。
                                </p>
                                <p className="text-xs text-gray-500">
                                  前端<strong>没有</strong>参与闲鱼登录协议，只负责展示截图并每 2 秒请求 <code className="text-[11px]">/qr-browser-login/check</code>；需要更稳可优先用「App 扫码」。
                                </p>
                              </div>
                              {qrBrowserHint ? (
                                <p className="text-xs text-gray-500 mb-3 bg-gray-50 p-3 rounded-xl text-left">{qrBrowserHint}</p>
                              ) : null}
                              {qrBrowserLoginUrl ? (
                                <a
                                  href={qrBrowserLoginUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block text-center text-sm text-blue-600 underline mb-4 break-all"
                                >
                                  {qrBrowserLoginUrl}
                                </a>
                              ) : null}
                              <div className="w-full min-h-[220px] max-h-[min(78vh,44rem)] bg-[#F7F8FA] rounded-[2rem] mx-auto flex items-start justify-center overflow-auto border-4 border-white shadow-inner mb-6 relative">
                                {qrStatus === 'loading' && (
                                  <div className="w-full min-h-[220px] flex items-center justify-center">
                                    <Loader2 className="w-10 h-10 text-[#FFE815] animate-spin" />
                                  </div>
                                )}
                                {(qrStatus === 'waiting' || qrStatus === 'scanned') && qrCodeUrl ? (
                                  <img
                                    key={qrImageSessionId || 'browser'}
                                    src={qrCodeUrl}
                                    alt="m.goofish.com 页面截图"
                                    className="w-full max-w-full h-auto"
                                  />
                                ) : null}
                                {(qrStatus === 'waiting' || qrStatus === 'scanned') && !qrCodeUrl ? (
                                  <p className="text-sm text-gray-500 p-6 text-center">未返回截图（可能为有头模式仅在服务器屏幕显示）。请在本机查看浏览器窗口完成扫码。</p>
                                ) : null}
                                {qrStatus === 'success' && (
                                  <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-green-600 animate-fade-in min-h-[8rem]">
                                    <Check className="w-10 h-10 mb-2" />
                                    <span className="font-bold">登录成功</span>
                                  </div>
                                )}
                                {qrStatus === 'error' && (
                                  <div className="flex flex-col items-center px-4 py-8">
                                    <span className="text-red-500 font-bold mb-2 text-center text-sm">会话失败或超时，请重试</span>
                                    {qrErrorDetail ? (
                                      <p className="text-xs text-gray-600 text-center mb-3 max-h-32 overflow-y-auto leading-relaxed">
                                        {qrErrorDetail}
                                      </p>
                                    ) : null}
                                    <button
                                      type="button"
                                      onClick={() => startQRLogin(true, 'browser')}
                                      className="text-xs bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1 hover:bg-gray-300"
                                    >
                                      <RefreshCw className="w-3 h-3" /> 重试
                                    </button>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 font-medium bg-gray-50 py-2 rounded-xl">
                                会话约 30 分钟；需 <code className="text-[11px]">playwright install chromium</code>。长时间无反应可先「重试」换一张新截图再扫。
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-gray-500 mb-8 font-medium">请打开闲鱼APP扫描下方二维码</p>
                              {qrStatus === 'scanned' && (
                                <p className="text-sm text-amber-700 font-bold mb-4 -mt-4">已在手机扫码，请在闲鱼内点确认登录</p>
                              )}

                              <div className="w-64 h-64 bg-[#F7F8FA] rounded-[2rem] mx-auto flex items-center justify-center overflow-hidden border-4 border-white shadow-inner mb-8 relative">
                                  {qrStatus === 'loading' && <Loader2 className="w-10 h-10 text-[#FFE815] animate-spin" />}
                                  {(qrStatus === 'waiting' || qrStatus === 'scanned') && qrCodeUrl && (
                                    <img
                                      key={qrImageSessionId || 'qr'}
                                      src={qrCodeUrl}
                                      alt="QR Code"
                                      className="w-full h-full p-2"
                                    />
                                  )}
                                  {qrStatus === 'success' && (
                                      <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center text-green-600 animate-fade-in">
                                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                             <Check className="w-8 h-8" />
                                          </div>
                                          <span className="font-bold text-lg">登录成功</span>
                                      </div>
                                  )}
                                  {qrStatus === 'error' && (
                                      <div className="flex flex-col items-center px-4">
                                          <span className="text-red-500 font-bold mb-2 text-center text-sm">二维码已失效或未找到会话，请重试</span>
                                          <button type="button" onClick={() => startQRLogin(true, 'http')} className="text-xs bg-gray-200 px-3 py-1 rounded-full flex items-center gap-1 hover:bg-gray-300"><RefreshCw className="w-3 h-3"/> 重试</button>
                                      </div>
                                  )}
                              </div>

                              <p className="text-xs text-gray-400 font-medium bg-gray-50 py-2 rounded-xl">本系统会保留会话约 15 分钟；扫码后请在闲鱼内尽快确认。若闲鱼提示码已过期，请点击重试重新生成。</p>
                            </>
                          )}
                      </div>
                  </div>
              </div>
          </div>,
          document.body
      )}

      {/* 编辑账号弹窗 */}
      {activeModal === 'edit' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900">编辑账号</h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 账号ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">账号ID</label>
                <input
                  type="text"
                  value={editingAccount.id}
                  disabled
                  className="w-full ios-input px-4 py-3 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">备注</label>
                <input
                  type="text"
                  value={editForm.remark}
                  onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
                  placeholder="为账号添加备注"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
              </div>

              {/* Cookie */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Cookie</label>
                <textarea
                  value={editForm.cookie}
                  onChange={(e) => setEditForm({ ...editForm, cookie: e.target.value })}
                  placeholder="更新账号Cookie"
                  className="w-full ios-input px-4 py-3 rounded-xl h-32 resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">当前Cookie长度: {editForm.cookie.length} 字符</p>
              </div>

              {/* 自动确认收货 */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    自动确认收货
                  </div>
                  <div className="text-xs text-gray-500">自动点击确认收货按钮</div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, auto_confirm: !editForm.auto_confirm })}
                  className={`w-14 h-8 rounded-full transition-colors duration-300 relative ${
                    editForm.auto_confirm ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      editForm.auto_confirm ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* 暂停时长 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  暂停处理时长（分钟）
                </label>
                <input
                  type="number"
                  value={editForm.pause_duration}
                  onChange={(e) => setEditForm({ ...editForm, pause_duration: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  max="1440"
                  className="w-full ios-input px-4 py-3 rounded-xl"
                />
                <p className="text-xs text-gray-500 mt-1">设置后会暂停处理该账号的订单，到时间后自动恢复</p>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI设置弹窗 */}
      {activeModal === 'ai-settings' && editingAccount && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                  <Bot className="w-6 h-6 text-purple-500" />
                  AI助手设置
                </h3>
                <p className="text-sm text-gray-500 mt-1">{editingAccount.nickname || editingAccount.remark || editingAccount.id}</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* 启用AI */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-500" />
                    启用AI自动回复
                  </div>
                  <div className="text-xs text-gray-500">AI将自动处理买家的砍价消息</div>
                </div>
                <button
                  type="button"
                  onClick={() => setAiSettings({ ...aiSettings, ai_enabled: !aiSettings.ai_enabled })}
                  className={`w-14 h-8 rounded-full transition-colors duration-300 relative ${
                    aiSettings.ai_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      aiSettings.ai_enabled ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* 砍价策略 */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">砍价策略</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣比例 (%)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_percent}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_percent: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：10表示最多降价10%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大折扣金额 (元)</label>
                    <input
                      type="number"
                      value={aiSettings.max_discount_amount}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_discount_amount: parseInt(e.target.value) || 0 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">例如：100表示最多降价100元</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">最大砍价轮次</label>
                    <input
                      type="number"
                      value={aiSettings.max_bargain_rounds}
                      onChange={(e) => setAiSettings({ ...aiSettings, max_bargain_rounds: parseInt(e.target.value) || 1 })}
                      className="w-full ios-input px-4 py-3 rounded-xl"
                      min="1"
                      max="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">买家最多可以砍价的次数</p>
                  </div>
                </div>
              </div>

              {/* 自定义提示词 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">自定义提示词（可选）</label>
                <textarea
                  value={aiSettings.custom_prompts}
                  onChange={(e) => setAiSettings({ ...aiSettings, custom_prompts: e.target.value })}
                  placeholder="输入自定义的AI回复规则或风格指引...&#10;&#10;例如：回复时保持礼貌专业、使用简洁的语言、强调产品质量等"
                  className="w-full ios-input px-4 py-3 rounded-xl h-40 resize-none"
                />
              </div>

              {/* AI如何工作 */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  AI如何工作
                </h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• 自动识别买家的砍价请求</li>
                  <li>• 根据设定的策略智能回复</li>
                  <li>• 在合理范围内同意降价或礼貌拒绝</li>
                  <li>• 保持专业友好的沟通风格</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={handleSaveAISettings}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Cookie登录弹窗 */}
      {activeModal === 'cookie-login' && createPortal(
        <div className="modal-overlay-centered">
          <div className="modal-container" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                  <Cookie className="w-6 h-6 text-amber-500" />
                  Cookie 登录
                </h3>
                <p className="text-sm text-gray-500 mt-1">粘贴 Cookie 即可添加账号，自动解析用户 ID</p>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {cookieLoginError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-xl text-sm font-medium">
                  {cookieLoginError}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Cookie 值</label>
                <textarea
                  value={cookieLoginValue}
                  onChange={(e) => setCookieLoginValue(e.target.value)}
                  placeholder="粘贴完整的 Cookie 字符串..."
                  rows={8}
                  className="w-full ios-input px-4 py-3 rounded-xl resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">从浏览器开发者工具 &gt; Application &gt; Cookies 中复制，系统会自动从中提取 unb 字段作为账号 ID</p>
              </div>
            </div>

            <div className="modal-footer">
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  onClick={async () => {
                    const value = cookieLoginValue.trim();
                    if (!value) {
                      setCookieLoginError('请粘贴 Cookie 值');
                      return;
                    }
                    // 从 Cookie 中解析 unb 字段
                    const unbMatch = value.match(/(?:^|;\s*)unb=([^;]+)/);
                    if (!unbMatch) {
                      setCookieLoginError('无法从 Cookie 中找到 unb 字段，请确保粘贴了完整的闲鱼 Cookie');
                      return;
                    }
                    const id = unbMatch[1].trim();
                    setSaving(true);
                    setCookieLoginError('');
                    try {
                      await addAccountByCookie(id, value);
                      setActiveModal(null);
                      loadAccounts();
                    } catch (error: any) {
                      setCookieLoginError(error?.message || error?.detail || '添加失败，请检查 Cookie 是否有效');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="flex-1 ios-btn-primary px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {saving ? '添加中...' : '添加账号'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AccountList;
